import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '@/api/api';
import { ENDPOINT } from '@/constants/endpoints';

type BillingPlanStatus = 'active' | 'trialing' | 'past_due' | 'canceled';
type BillingCycle = 'monthly' | 'annual';
type InvoicePaymentStatus = 'paid' | 'pending' | 'failed';
export type BillingSetupPlanKey = 'monthly' | 'annual';

export type BillingAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

export type BillingPlan = {
  status: BillingPlanStatus;
  monthlyUsers: number;
  renewalAmount: number;
  currency: string;
  pricePerUser: number;
  nextChargeDate: string;
  billingCycle: BillingCycle;
};

export type BillingInvoiceInfo = {
  companyName: string;
  billingEmail: string;
  companyAddress: string | null;
  address?: BillingAddress | null;
  taxId: string | null;
  paymentMethod: string | null;
  lastPaymentDate: string | null;
};

export type RecentInvoice = {
  id: string;
  paymentDate: string;
  status: InvoicePaymentStatus;
  amount: number;
  currency: string;
  viewUrl: string | null;
  downloadUrl: string | null;
};

export type BillingStatusResponse = {
  plan: BillingPlan;
  invoiceInfo: BillingInvoiceInfo;
  recentInvoices: RecentInvoice[];
};

type BillingPortalResponse = {
  url: string;
};

export type BillingSetupPrice = {
  currency: string;
  unitAmount: number;
};

export type BillingSetupPlan = {
  interval: 'month' | 'year';
  priceId: string;
  prices: BillingSetupPrice[];
};

export type BillingSetupResponse = {
  annual: BillingSetupPlan;
  defaultCurrency: string;
  employeeCount: number;
  hrisConnected: boolean;
  monthly: BillingSetupPlan;
  supportedCurrencies: string[];
};

export type SubscribeBillingPayload = {
  plan: BillingSetupPlanKey;
  currency: string;
  returnUrl: string;
};

type SubscribeBillingResponse = {
  clientSecret: string;
};

export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery: axiosBaseQuery,
  tagTypes: ['Billing'],
  endpoints: builder => ({
    getBillingSetup: builder.query<BillingSetupResponse, void>({
      query: () => ({
        url: ENDPOINT.BILLING.GET_SETUP(),
        method: 'GET',
      }),
      providesTags: ['Billing'],
    }),
    getBillingStatus: builder.query<BillingStatusResponse, void>({
      query: () => ({
        url: ENDPOINT.BILLING.GET_STATUS(),
        method: 'GET',
      }),
      providesTags: ['Billing'],
    }),
    getBillingPortal: builder.query<BillingPortalResponse, void>({
      query: () => ({
        url: ENDPOINT.BILLING.GET_PORTAL(),
        method: 'GET',
      }),
      providesTags: ['Billing'],
    }),
    subscribeBilling: builder.mutation<SubscribeBillingResponse, SubscribeBillingPayload>({
      query: body => ({
        url: ENDPOINT.BILLING.SUBSCRIBE(),
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetBillingStatusQuery,
  useGetBillingPortalQuery,
  useLazyGetBillingPortalQuery,
  useGetBillingSetupQuery,
  useSubscribeBillingMutation,
} = billingApi;
